/**
 * 🔴 **HTTP 客户端 —— 前端唯一的出网处**
 *
 * ## 两条铁律（逐条对应架构原文）
 *
 * | 铁律 | 落实 |
 * |---|---|
 * | `X-Ledger-Id` 是账本上下文的**唯一来源**（架构 §6.2） | 每请求由 `ledgerStore` 注入；**没有**任何地方从 JWT 里推 |
 * | **不得在端侧重算口径**（规格 §22.2 派生优先 / 铁律 24） | 本文件**只搬运**，不做任何金额聚合；余额、净资产、分组计数一律用服务端下发的字段 |
 *
 * ## ⚠️ 为什么不用 axios
 * 原生 `fetch` 足够（无拦截器需求：两个头 + 一次 JSON 解析），少一个依赖。
 *
 * ## 🔴 错误处理为什么不是"弹个 toast 就完"
 * 服务端所有失败都是**统一错误体**（`code` + `message` + 可选 `details`），
 * 而 `message` 是**给人看的中文**（例如"现金价值（1200 分）不得大于保额（1000 分）——常见原因是把元当分填了"）。
 * ⇒ 端侧**原样展示 `message`**，不自己编文案 —— 编文案就等于把服务端的口径又写了一遍（会漂移）。
 */

/** 服务端统一错误体（见 `platform/errors`） */
export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
}

/** 请求失败的统一异常 —— 携带 HTTP 状态与原始错误体，供页面分情形处理 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiErrorBody,
  ) {
    super(body.message ?? `请求失败（HTTP ${status}）`);
    this.name = 'ApiError';
  }

  /** 401：token 缺失/过期 ⇒ 上层应清会话并跳登录 */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 403：**非本账本成员**（架构 §6.2）—— 与"没登录"必须区分开 */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/** 会话（token + 当前账本）—— 由 `session.ts` 落到 `localStorage` */
export interface Session {
  token: string;
  /** 当前账本 id；注册响应会直接带回一本新账本 */
  ledgerId: string;
  /**
   * 🆕 **本人 `users.id`**（批次 `66`）—— 实时提示条用它**忽略自己触发的操作**。
   *
   * ## 🔴 为什么"忽略自己"必须靠 id 而不是靠名字
   *
   * `RealtimeBasePayload.actorUserId` 是**记录人**的 `users.id`（契约原文）。
   * 若端侧拿 `userName` 比对，则**同名成员**（一家两口都叫"沈琰"不必，但展示名可重复 ——
   * 契约层从没承诺 `name` 唯一）会把自己的操作当成别人的 ⇒ **自弹提示条**；
   * 反过来改名后旧会话会对不上。⇒ 唯一可靠判据是 **id**。
   *
   * ⚠️ 老会话（升级前写入 `localStorage` 的）**没有本字段** ⇒ `loadSession()` 回退成 `''`，
   * 此时**一律当"不是我"**（宁可多弹一次，也不静默吞掉别人的改动 —— 见 `loadSession`）。
   */
  userId: string;
  /** 展示用（顶栏 / 账本切换器） */
  userName: string;
  ledgerName: string;
}

/** 由 `main.tsx` 在启动时注入：读当前会话（未登录返回 `null`） */
type SessionGetter = () => Session | null;
let getSession: SessionGetter = () => null;

/** 由 `main.tsx` 注入：收到 401 时清会话（跳转由路由层用状态驱动，不在此处硬跳） */
let onUnauthorized: () => void = () => {};

export function configureApi(opts: { getSession: SessionGetter; onUnauthorized: () => void }): void {
  getSession = opts.getSession;
  onUnauthorized = opts.onUnauthorized;
}

/**
 * 发一次请求。
 *
 * @param path 以 `/api` 开头的路径（如 `/api/accounts`）
 * @param init `method` / `json` / 额外头
 */
export async function api<T>(
  path: string,
  init: { method?: string; json?: unknown; headers?: Record<string, string> } = {},
): Promise<T> {
  const session = getSession();
  const headers: Record<string, string> = { ...(init.headers ?? {}) };
  if (session !== null) {
    headers['Authorization'] = `Bearer ${session.token}`;
    // 🔴 账本上下文只来自这个头（架构 §6.2）；未登录时不发（如注册 / 登录本身）
    headers['X-Ledger-Id'] = session.ledgerId;
  }
  const request: RequestInit = { method: init.method ?? 'GET', headers };
  if (init.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    request.body = JSON.stringify(init.json);
  }

  const res = await fetch(path, request);
  return (await parseOrThrow(res)) as T;
}

/**
 * 把响应读成 `T`，失败时抛 `ApiError`（**`api` / `apiUpload` 共用**）。
 *
 * 🔴 抽出来的理由：错误体解析 + 401 回调是**同一套语义**。若上传自己再写一遍，
 * 就出现第二处「401 时不清理会话」的地方 —— 症状是"上传账单后一直 401 但不跳登录"。
 */
async function parseOrThrow(res: Response): Promise<unknown> {
  // 204 等无体响应：调用方用 `api<void>` 即可
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      // 服务端理论上永远回 JSON；真收到非 JSON（如反代 502 页面）时给出可读错误
      parsed = { message: text.slice(0, 200) };
    }
  }

  if (!res.ok) {
    const body = (parsed ?? {}) as ApiErrorBody;
    if (res.status === 401) onUnauthorized();
    throw new ApiError(res.status, body);
  }
  return parsed;
}

/**
 * 🔴 **上传原始字节**（唯一调用方：账单导入预览 `POST /api/imports/preview`）。
 *
 * ## 为什么不能走 `api({ json })`
 *
 * 账单原文是**字节**，不是 JSON：
 * - 微信/支付宝导出的 CSV 常是 **GBK**，一旦先按 UTF-8 解码成字符串再发，乱码**不可逆**；
 * - 服务端为该路由挂的是 raw parser（`main.ts`），只认 `text/csv` / `application/octet-stream`。
 *
 * ⇒ 请求体原样传 `File`（浏览器里 `File` 是 `Blob`，可直接当 `BodyInit`），**不发 `Content-Type: application/json`**。
 * 这两个头（`Authorization` / `X-Ledger-Id`）仍由本文件统一注入（架构 §6.2：账本上下文只有一个来源）。
 */
export async function apiUpload<T>(
  path: string,
  body: Blob,
  contentType: string,
): Promise<T> {
  const session = getSession();
  const headers: Record<string, string> = { 'Content-Type': contentType };
  if (session !== null) {
    headers['Authorization'] = `Bearer ${session.token}`;
    headers['X-Ledger-Id'] = session.ledgerId;
  }
  const res = await fetch(path, { method: 'POST', headers, body });
  return (await parseOrThrow(res)) as T;
}

/**
 * 🔴 **受鉴权的原始响应** —— 唯一调用方是**文件下载**（`api/download.ts`）。
 *
 * ## 为什么不能各下载处自己 `fetch`
 *
 * 下载也要带 `Authorization` + `X-Ledger-Id`（架构 §6.2 的账本上下文**只有这一个来源**）。
 * 若在下载代码里自己拼头，就出现了**第二处**拼这两个头的地方 ⇒ 将来改鉴权（如换头名 / 加刷新）
 * 会漏掉下载这条路径，而它的症状是"点导出报 403"——看起来像权限问题，不是拼头问题。
 * ⇒ 一律经本函数（与 `api()` 共用同一个 `getSession`）。
 *
 * ## 与 `api()` 的两点差别（都必要）
 *
 * 1. **不读 body**（下载体可能是几 MB 的 CSV/JSON，读成 string 会白占内存又没用；
 *    要它的是调用方的 `res.blob()`）；
 * 2. **不解析 JSON** —— 失败时**才**读一次 body 拿统一错误体（`ApiError`）。
 */
export async function apiRaw(path: string): Promise<Response> {
  const session = getSession();
  const headers: Record<string, string> = {};
  if (session !== null) {
    headers['Authorization'] = `Bearer ${session.token}`;
    headers['X-Ledger-Id'] = session.ledgerId;
  }
  const res = await fetch(path, { headers });
  if (!res.ok) {
    let body: ApiErrorBody = {};
    try {
      body = JSON.parse(await res.text()) as ApiErrorBody;
    } catch {
      body = {};
    }
    if (res.status === 401) onUnauthorized();
    throw new ApiError(res.status, body);
  }
  return res;
}

/**
 * 🔴 **带显式 token 的请求** —— 只用于"会话尚未写入"的极少数场景。
 *
 * ## 为什么必须有这么一个旁路（而不是"先写会话再查"）
 *
 * 登录成功后要取账本列表（`login` 响应**不含** `ledgerId`），但此刻会话还没建立。
 * 两条路：
 * - **A（本函数）**：带 token 直接 GET /ledgers ⇒ 取到账本名后**一次性**写入会话，无中间态；
 * - B：先写入"账本名未知"的会话 ⇒ 触发一次渲染，顶栏显示空账本名，再回填。
 *
 * 选 A：少一次渲染闪烁，且**不制造"会话已存在但字段不全"的中间态**（那种中间态最容易被后来的人
 * 当成"合法状态"而在别处依赖它）。
 *
 * ⚠️ **它只允许在登录链路里用** —— 业务代码一律走 `api()`（否则会绕过 `X-Ledger-Id` 纪律，
 * 而那正是架构 §6.2 的落点）。
 */
export async function apiWithToken<T>(path: string, token: string): Promise<T> {
  const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
  const text = await res.text();
  const parsed: unknown = text === '' ? undefined : JSON.parse(text);
  if (!res.ok) throw new ApiError(res.status, (parsed ?? {}) as ApiErrorBody);
  return parsed as T;
}
